<template>
  <custom-popup
    v-model="dialog"
    :cons="{ title: $t('qrOrdersList.title') }"
    hide-confirm
    max-width="1024"
    reloadable
    @close="resetFields"
    :key="$i18n.locale"
    @reload="handleReload"
  >
    <v-card class="pa-4" flat>
      <v-progress-linear v-show="loading" class="mb-2" indeterminate />
      <vue-table
        :data="data"
        :headers="headers"
        :loading.sync="loading"
        @change="onPagination"
      >
        <template v-slot:filters="{ props, perPage }">
          <v-row no-gutters>
            <v-col class="pa-2" cols="12" lg="2" md="3">
              <v-select
                v-model="props.length"
                :items="perPage"
                class="rounded-lg"
                dense
                hide-details
                :label="$t('qrOrdersList.perPage')"
                outlined
              />
            </v-col>
            <v-col class="pa-2" cols="12" lg="3" md="4">
              <v-text-field
                v-model="props.search"
                append-icon="mdi-magnify"
                class="rounded-lg"
                clearable
                dense
                hide-details
                :label="$t('qrOrdersList.search')"
                outlined
              />
            </v-col>
            <v-col class="pa-2" cols="12" lg="3" md="3">
              <v-autocomplete
                v-model="props.status"
                append-icon="mdi-list-status"
                class="rounded-lg"
                clearable
                :items="statusItems"
                item-text="text"
                item-value="value"
                dense
                :no-data-text="
                  statusItems.length === 0
                    ? $t('qrOrdersList.noStatus')
                    : $t('qrOrdersList.loading')
                "
                hide-details
                :label="$t('qrOrdersList.orderColumns.status')"
                outlined
                @change="onFilterChange('status', $event)"
              >
                <template v-slot:selection="{ item }">
                  <v-icon left small color="primary">{{ item.icon }}</v-icon>
                  {{ item.text }}
                </template>
                <template v-slot:item="{ item }">
                  <v-icon left small color="primary">{{ item.icon }}</v-icon>
                  {{ item.text }}
                </template>
              </v-autocomplete>
            </v-col>
            <v-col class="pa-2" cols="12" lg="3" md="3">
              <v-autocomplete
                v-model="props.shipping"
                append-icon="mdi-truck"
                class="rounded-lg"
                clearable
                :items="shippingTypes"
                item-text="text"
                item-value="value"
                dense
                :no-data-text="$t('qrOrdersList.noShippingType')"
                hide-details
                :label="$t('qrOrdersList.orderColumns.shippingType')"
                outlined
                @change="onFilterChange('shipping', $event)"
              >
                <template v-slot:selection="{ item }">
                  <v-icon left small color="primary">{{ item.icon }}</v-icon>
                  {{ item.text }}
                </template>
                <template v-slot:item="{ item }">
                  <v-icon left small color="primary">{{ item.icon }}</v-icon>
                  {{ item.text }}
                </template>
              </v-autocomplete>
            </v-col>
            <v-col class="pa-2" cols="12" lg="3" md="3">
              <v-autocomplete
                v-model="props.pay"
                append-icon="mdi-credit-card"
                class="rounded-lg"
                clearable
                :items="paymentTypes"
                item-text="text"
                item-value="value"
                dense
                :no-data-text="$t('qrOrdersList.noPaymentType')"
                hide-details
                :label="$t('qrOrdersList.orderColumns.paymentType')"
                outlined
                @change="onFilterChange('pay', $event)"
              >
                <template v-slot:selection="{ item }">
                  <v-icon left small color="primary">{{ item.icon }}</v-icon>
                  {{ item.text }}
                </template>
                <template v-slot:item="{ item }">
                  <v-icon left small color="primary">{{ item.icon }}</v-icon>
                  {{ item.text }}
                </template>
              </v-autocomplete>
            </v-col>
            <v-col class="pa-2" cols="12" lg="2" md="3">
              <v-select
                v-model="props.column"
                :items="columnOptions"
                :label="$t('qrOrdersList.sortColumn')"
                dense
                outlined
                class="rounded-lg"
                @change="onPagination"
              />
            </v-col>
            <v-col class="pa-2" cols="12" lg="2" md="3">
              <v-select
                v-model="props.dir"
                :items="dirOptions"
                :label="$t('qrOrdersList.sortDirection')"
                dense
                outlined
                class="rounded-lg"
                @change="onPagination"
              />
            </v-col>
          </v-row>
        </template>

        <template v-slot:index="{ item }">
          {{ persian(item?.index + 1) }}
        </template>

        <template v-slot:id="{ item }">
          {{ persian(item?.id) }}
        </template>

        <template v-slot:user_name="{ item }">
          {{ item.user?.name + " " + item.user?.family }}
        </template>

        <template v-slot:mobile="{ item }">
          {{ item.user?.mobile || "-" }}
        </template>

        <template v-slot:payment_type="{ item }">
          {{ pt(item?.payment_type) }}
        </template>

        <template v-slot:shipping_type="{ item }">
          {{ st(item) }}
        </template>

        <template v-slot:price="{ item }">
          {{ persian(price(item?.price)) + " " + $t("qrOrdersList.currency") }}
        </template>

        <template v-slot:created_at="{ item }">
          {{ datetime(item.created_at, "datetime") || "-" }}
        </template>

        <template v-slot:status="{ item }">
          <v-chip :color="orderStatus(item).color" small>
            <v-icon left :color="orderStatus(item).color" x-small>
              {{ orderStatus(item)?.icon }}
            </v-icon>
            {{ orderStatus(item)?.text }}
          </v-chip>
        </template>

        <template v-slot:action="{ item }">
          <orders-list-button
            :data="item"
            :meta="getMetaButtons(item)"
            v-if="item.status !== 'rejected'"
          />

          <div v-if="item.status === 'rejected'">
            <v-icon left small>mdi-close</v-icon>
          </div>
        </template>
      </vue-table>

      <custom-popup
        v-model="showProducts"
        :cons="{ title: $t('qrOrdersList?.title') }"
        @close="productsList = []"
      >
        <v-card class="pa-4" flat>
          <vue-table
            :data="{ data: productsList }"
            :headers="productsHeaders"
            :loading.sync="loading"
          >
            <template v-slot:product_id="{ item }">
              {{ item.product?.id || "-" }}
            </template>
            <template v-slot:image="{ item }">
              <span v-html="renderProductImage(item)"></span>
            </template>
            <template v-slot:product_name="{ item }">
              {{ item.product?.name || "-" }}
            </template>
            <template v-slot:unit_price="{ item }">
              {{
                persian(price(item.product?.price)) +
                " " +
                $t("qrOrdersList.currency")
              }}
            </template>
            <template v-slot:quantity="{ item }">
              {{ item.count || "-" }}
            </template>
            <template v-slot:total_price="{ item }">
              {{
                persian(
                  price(Number(item.product?.price) * Number(item.count))
                ) +
                " " +
                $t("qrOrdersList.currency")
              }}
            </template>
          </vue-table>
        </v-card>
      </custom-popup>

      <custom-popup
        v-model="showFactor"
        :cons="{ title: $t('qrOrdersList.factorTitle') }"
        hide-confirm
        max-width="350"
      >
        <menu-order-factor :info="info" :order="orderFactor" />
        <v-divider class="mx-4 mb-0" />
        <v-card-actions>
          <v-btn class="rounded-lg blue" dark depressed @click="printFactor">
            {{ $t("qrOrdersList.printFactor") }}
          </v-btn>
        </v-card-actions>
      </custom-popup>

      <custom-popup
        v-model="showStatusChange"
        :cons="{ title: $t('qrOrdersList.changeStatusTitle') }"
        max-width="350"
      >
        <v-card class="pa-4" flat>
          <v-autocomplete
            v-model="newStatus"
            :items="statusItems2"
            item-text="text"
            item-value="value"
            dense
            outlined
            :label="$t('qrOrdersList.selectNewStatus')"
            class="rounded-lg"
            @change="confirmStatusChange"
          >
            <template v-slot:selection="{ item }">
              <v-icon left small color="primary">{{ item.icon }}</v-icon>
              {{ item.text }}
            </template>
            <template v-slot:item="{ item }">
              <v-icon left small color="primary">{{ item.icon }}</v-icon>
              {{ item.text }}
            </template>
          </v-autocomplete>
        </v-card>
      </custom-popup>
    </v-card>
     <!-- پاپ‌آپ بررسی تراکنش کارت به کارت -->
<custom-popup
  v-model="showTransactionCheck"
  :cons="{ title: 'بررسی فیش واریزی' }"
  hide-confirm
  max-width="500"
>
  <v-card class="pa-4" flat>
    <v-progress-linear v-if="loadingTransaction" indeterminate color="primary" class="mb-4" />
    
    <div v-if="transactionDetail">
      <v-alert v-if="!transactionDetail.path" type="warning" dense>
        تصویر فیش واریزی توسط کاربر آپلود نشده است.
      </v-alert>

      <v-img
        v-if="transactionDetail.path"
        :src="transactionDetail.path"
        class="rounded-lg mb-4"
        max-height="400"
        contain
        @click="openImage(transactionDetail.path)"
      >
        <template v-slot:placeholder>
          <v-row class="fill-height ma-0" align="center" justify="center">
            <v-progress-circular indeterminate color="grey lighten-5" />
          </v-row>
        </template>
      </v-img>

      <v-list dense class="grey lighten-4 rounded-lg mb-4">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-subtitle>وضعیت تراکنش:</v-list-item-subtitle>
            <v-list-item-title>
            <v-chip :color="orderStatus(transactionDetail.status).color" small>
            <v-icon left :color="orderStatus(transactionDetail).color" x-small>
              {{ orderStatus(transactionDetail)?.icon }}
            </v-icon>
            {{ orderStatus(transactionDetail)?.text }}
          </v-chip>

            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <div v-if="transactionDetail.status === 'pending'" class="d-flex flex-row justify-space-between">
          <v-btn  class="rounded-lg btn-get-code" @click="updateTransactionStatus('accept')">
            <v-icon left>mdi-check</v-icon> تایید واریز
          </v-btn>
          <v-spacer/>
          <v-btn outlined class="rounded-lg btn-delete" @click="updateTransactionStatus('reject')">
            <v-icon left>mdi-close</v-icon> عدم تایید
          </v-btn>
        </div>
    </div>
  </v-card>
</custom-popup>
  </custom-popup>
</template>

<script>
import ordersListButton from "@/view/components/adapters/qrList/ordersListButton.vue";
import MenuOrderFactor from "./menuOrderFactor.vue";
import manageButton from "@/view/pages/button.vue";
import { mapGetters } from "vuex";
import g from "file-saver";

export default {
  name: "qrOrdersList",
  components: { MenuOrderFactor, ordersListButton, manageButton },

  props: {
    value: Boolean,
    address: String,
    isQrlink: 
    {
      type:Boolean,
      default:false
    }
  },

  data() {
    return {
      showTransactionCheck: false,
      loadingTransaction: false,
      transactionDetail: null,
      info:{},
      data: {},
      tableProps: {
        search: "",
        length: 10,
        column: "id",
        dir: "desc",
        page: 1,
        status: null,
        shipping: null,
        pay: null,
      },
      loading: false,
      showProducts: false,
      productsList: [],
      showFactor: false,
      orderFactor: {},
      showStatusChange: false,
      newStatus: null,
      currentOrder: null,
      flipped: false,
      statusItems: [
        {
          text: this.$t("qrOrdersList.status.all"),
          value: null,
          icon: "mdi-filter",
        },
        {
          text: this.$t("qrOrdersList.status.pending"),
          value: "pending",
          icon: "mdi-clock-outline",
        },
        {
          text: this.$t("qrOrdersList.status.accepted.paid"),
          value: "accepted",
          icon: "mdi-check",
        },
        {
          text: this.$t("qrOrdersList.status.rejected"),
          value: "rejected",
          icon: "mdi-close",
        },
        {
          text: this.$t("qrOrdersList.status.delivered"),
          value: "delivered",
          icon: "mdi-close",
        },
      ],
      statusItems2: [
        {
          text: this.$t("qrOrdersList.status.pending"),
          value: "pending",
          icon: "mdi-clock-outline",
        },
        {
          text: this.$t("qrOrdersList.status.accepted.paid"),
          value: "accepted",
          icon: "mdi-check",
        },
        {
          text: this.$t("qrOrdersList.status.rejected"),
          value: "rejected",
          icon: "mdi-close",
        },
        {
          text: this.$t("qrOrdersList.status.delivered"),
          value: "delivered",
          icon: "mdi-close",
        },
      ],
      shippingTypes: [
        {
          value: "courier",
          text: this.$t("qrOrdersList.shippingType.courier"),
          icon: "moped-electric-outline",
        },
        {
          value: "post",
          text: this.$t("qrOrdersList.shippingType.post"),
          icon: "truck-fast",
        },
        {
          value: "place",
          text: this.$t("qrOrdersList.shippingType.place"),
          icon: "map-marker-outline",
        },
      ],
      paymentTypes: [
        {
          value: "online",
          text: this.$t("qrOrdersList.paymentType.online"),
          icon: "account-credit-card-outline",
        },
         {
          value: "card2card",
          text: this.$t("qrOrdersList.paymentType.card2card"),
          icon: "credit-card-multiple-outline",
        },
        {
          value: "place",
          text: this.$t("qrOrdersList.paymentType.place"),
          icon: "credit-card-marker-outline",
        },
      ],
      columnOptions: [
        { text: this.$t("qrOrdersList.orderColumns.id"), value: "id" },
        {
          text: this.$t("qrOrdersList.orderColumns.createdAt"),
          value: "created_at",
        },
      ],
      dirOptions: [
        { text: this.$t("qrOrdersList.sortDirectionDesc"), value: "desc" },
        { text: this.$t("qrOrdersList.sortDirectionAsc"), value: "asc" },
      ],
      headers: [
        {
          value: "index",
          text: this.$t("qrOrdersList.orderColumns.index"),
          align: "center",
          sortable: false,
        },
        {
          value: "id",
          text: this.$t("qrOrdersList.orderColumns.id"),
          align: "center",
          sortable: true,
        },
        {
          value: "user_name",
          text: this.$t("qrOrdersList.orderColumns.customerName"),
          align: "center",
          sortable: false,
        },
        {
          value: "mobile",
          text: this.$t("qrOrdersList.orderColumns.mobile"),
          align: "center",
          sortable: true,
        },
        {
          value: "payment_type",
          text: this.$t("qrOrdersList.orderColumns.paymentType"),
          align: "center",
          sortable: false,
        },
        {
          value: "shipping_type",
          text: this.$t("qrOrdersList.orderColumns.shippingType"),
          align: "center",
          sortable: false,
        },
        {
          value: "price",
          text: this.$t("qrOrdersList.orderColumns.price"),
          align: "center",
          sortable: true,
        },
        {
          value: "created_at",
          text: this.$t("qrOrdersList.orderColumns.createdAt"),
          align: "center",
          sortable: false,
        },
        {
          value: "status",
          text: this.$t("qrOrdersList.orderColumns.status"),
          align: "center",
          sortable: false,
        },
        {
          value: "action",
          text: this.$t("qrOrdersList.orderColumns.actions"),
          align: "center",
          sortable: false,
        },
      ],
      productsHeaders: [
        {
          value: "product_id",
          text: this.$t("qrOrdersList.productsColumns.productId"),
          align: "center",
          sortable: false,
        },
        {
          value: "image",
          text: this.$t("qrOrdersList.productsColumns.image"),
          align: "center",
          sortable: false,
        },
        {
          value: "product_name",
          text: this.$t("qrOrdersList.productsColumns.productName"),
          align: "center",
          sortable: false,
        },
        {
          value: "unit_price",
          text: this.$t("qrOrdersList.productsColumns.unitPrice"),
          align: "center",
          sortable: false,
        },
        {
          value: "quantity",
          text: this.$t("qrOrdersList.productsColumns.quantity"),
          align: "center",
          sortable: false,
        },
        {
          value: "total_price",
          text: this.$t("qrOrdersList.productsColumns.totalPrice"),
          align: "center",
          sortable: false,
        },
      ],
    };
  },

  computed: {
    ...mapGetters("ribbon", ["user"]),
    metaButtons() {
      const buttons = [
        {
          text: "products",
          label: this.$t("qrOrdersList.actions.products"),
          icon: "view-list-outline",
          class: "btn-outline-danger",
          action: (data) => this.actions(data, "products"),
        },
        {
          text: "print",
          label: this.$t("qrOrdersList.actions.print"),
          icon: "printer-outline",
          class: "btn-outline-warning",
          action: (data) => this.actions(data, "print"),
        },
        {
          text: "changestatus",
          label: this.$t("qrOrdersList.actions.status"),
          icon: "list-status",
          class: "btn-outline-success",
          action: (data) => this.actions(data, "changestatus"),
        },
        
        
      ];
    
      if (this.$i18n.locale === "fa") {
        buttons.splice(2, 0, {
          text: "share",
          label: this.$t("qrOrdersList.actions.share"),
          icon: "share-variant-outline",
          class: "btn-outline-primary",
          action: (data) => this.actions(data, "share"),
        });
      }
      return { buttons };
    },
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    url() {
      if (!this.info || !this.info?.domain?.name || !this.info?.identifier) {
        return "";
      }
      return `${this.info.domain?.name}/${this.info.identifier}`.replace(
        /\/+/g,
        "/"
      );
    },
  },

  watch: {
    "$i18n.locale": {
      handler() {
        this.$forceUpdate();
      },
      immediate: true,
      deep: true,
    },
    value(val) {
      if (val) {
        this.fetchData().then(() => {
          this.fetch();
        });
      }
    }, 
   },


  methods: {
    openImage(url) {
  window.open(url, "_blank");
    },
      getMetaButtons(item) {
    const buttons = [...this.metaButtons.buttons];

    if (item.payment_type === "card2card") {
      buttons.push({
        text: "checktransaction",
        label: "بررسی تراکنش",
        icon: "credit-card-check-outline",
        class: "btn-outline-info",
        action: (data) => this.actions(data, "checktransaction"),
      });
    }

    return { buttons };
  },
      checktransactionAction(data) {
        this.currentOrder = data;
        this.showTransactionCheck = true;
        this.fetchTransactionInfo(data.id);
      },

      fetchTransactionInfo(orderId) {
        this.loadingTransaction = true;
        this.transactionDetail = null;
        this.$api.get(`/v1/testQr/card/transactions/${this.info.id}/${orderId}`)
          .then(({ data }) => {
            this.transactionDetail = data.data; 
          })
          .catch(() => {
            this.$toast.error("خطا در دریافت اطلاعات تراکنش");
            this.showTransactionCheck = false;
          })
          .finally(() => (this.loadingTransaction = false));
      },

      updateTransactionStatus(status) {
        this.loadingTransaction = true;
        const qrId = this.info.id;
        const transId = this.transactionDetail.id; 

        this.$api.put(`/v1/testQr/card/transaction/${qrId}/${transId}`, {
          status: status
        })
        .then(() => {
          this.$toast.success("وضعیت تراکنش با موفقیت بروزرسانی شد");
          this.showTransactionCheck = false;
          this.fetch(); // ریلود کردن لیست اصلی سفارشات
        })
        .catch(() => {
          this.$toast.error("خطا در ثبت وضعیت");
        })
        .finally(() => (this.loadingTransaction = false));
      },
     fetchData() {
      this.loading = true;
      let qrurl ='';
            if (this.isQrlink) {
       qrurl = this.qr_id ? `/v1/testQr/find1/${this.qr_id}` : null;
      }
      qrurl = this.address ? `/v1/testQr/find/${this.address}` : null;

      return this.$api
        .get(qrurl)
        .then(({ data }) => {
          this.info = data.data;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    handleReload() {
      this.$nextTick(() => {
        this.fetchData();
        this.fetch();
      });
    },
    swipe(dir) {
      if (dir === "Right" && !this.flipped) {
        this.flipped = true;
      } else if (dir === "Left" && this.flipped) {
        this.flipped = false;
      }
    },
    fetch(props = this.tableProps) {
      this.loading = true;
      this.$api
        .get(`/v1/testQr/find/orders/${this.url}`, {
          params: {
            search: props.search,
            // length: props.length,
            column: props.column,
            dir: props.dir,
            // page: props.page,
            status: props.status,
            shipping: props.shipping,
            pay: props.pay,
          },
        })
        .then(({ data }) => {
          this.data = data.data;
        })
        .catch((err) => {
          this.$toast.error(this.$t("qrOrdersList.fetchError"));
        })
        .finally(() => (this.loading = false));
    },
    onPagination(e) {
      if (
        e.page !== this.tableProps.page ||
        e.length !== this.tableProps.length ||
        e.search !== this.tableProps.search ||
        e.status !== this.tableProps.status ||
        e.shipping !== this.tableProps.shipping ||
        e.pay !== this.tableProps.pay ||
        e.column !== this.tableProps.column ||
        e.dir !== this.tableProps.dir
      ) {
        this.tableProps.page = e.page;
        this.tableProps.length = e.length;
        this.tableProps.search = e.search;
        this.tableProps.status = e.status;
        this.tableProps.shipping = e.shipping;
        this.tableProps.pay = e.pay;
        this.tableProps.column = e.column;
        this.tableProps.dir = e.dir;
        this.$nextTick(() => {
          this.fetch();
        });
      }
    },
    onFilterChange(filter, value) {
      this.tableProps[filter] = value === "" ? null : value;
      this.fetch();
    },
    st(t) {
      switch (t.shipping_type) {
        case "courier":
          return this.$t("qrOrdersList.shippingType.courier");
        case "reservation":
          return `${this.$t(
            "qrOrdersList.shippingType.reservation"
          )}<br>Tables: ${t.tables?.map((item) => item.table_id).join(", ")}`;
        case "post":
          return this.$t("qrOrdersList.shippingType.post");
        case "place":
          return this.$t("qrOrdersList.shippingType.place");
        default:
          return this.$t("qrOrdersList.shippingType.unknown");
      }
    },
    pt(t) {
      switch (t) {
        case "online":
          return this.$t("qrOrdersList.paymentType.online");
        case "place":
          return this.$t("qrOrdersList.paymentType.place");
        case "card2card":
          return this.$t("qrOrdersList.paymentType.card2card");
        default:
          return this.$t("qrOrdersList.paymentType.unknown");
      }
    },
    resetFields() {
      this.data = {};
      this.productsList = [];
      this.tableProps = {
        search: "",
        length: 10,
        column: "id",
        dir: "desc",
        page: 1,
        status: null,
        shipping: null,
        pay: null,
      };
      this.flipped = false;
      this.showStatusChange = false;
      this.newStatus = null;
      this.currentOrder = null;
    },
    orderStatus(data) {
      let s = data.status;
      switch (s) {
        case "pending":
          return {
            text: this.$t("qrOrdersList.status.pending"),
            color: "amber-lighten-3",
            icon: "mdi-clock-outline",
          };
        case "accepted":
          case "accept":
          return {
            text:
              data.payment_type === "place"
                ? this.$t("qrOrdersList.status.accepted.place")
                : this.$t("qrOrdersList.status.accepted.paid"),
            color:
              data.payment_type === "place"
                ? "cyan-lighten-4"
                : "teal-lighten-4",
            icon: "mdi-check",
          };
        case "rejected":
          return {
            text: this.$t("qrOrdersList.status.rejected"),
            color: "red-lighten-4",
            icon: "mdi-close",
          };
        default:
          return {
            text: s,
            color: "grey-lighten-3",
            icon: "mdi-help-circle",
          };
      }
    },
    actions(data, type) {
      this[type + "Action"](data);
    },
    productsAction(data) {
      this.showProducts = true;
      setTimeout(() => {
        this.productsList = data.items;
      });
    },
    printAction(data) {
      this.showFactor = true;
      this.orderFactor = data;
    },
    changestatusAction(data) {
      this.currentOrder = data;
      this.showStatusChange = true;
      this.newStatus = null;
    },
    confirmStatusChange(status) {
      if (!this.currentOrder || !status) return;
      this.$api
        .post(`/v1/testQr/change/qr-order-status/${this.url}`, {
          id: this.currentOrder.id,
          status,
        })
        .then(() => {
          this.$toast.success(this.$t("qrOrdersList.statusChanged"));
          this.fetch();
          this.showStatusChange = false;
          this.newStatus = null;
          this.currentOrder = null;
        })
        .catch(() => {
          this.$toast.error(this.$t("qrOrdersList.statusChangeError"));
        });
    },

    shareAction(data) {
      navigator.clipboard.writeText(data.share_link || this.url);
      this.$toast.success(this.$t("qrOrdersList.linkCopied"));
      let token = localStorage.getItem("id_token");

      let user = this.user?.name ?? "";
      let link = "";

      if (this.user?.family) user += " " + this.user.family;
      if (this.info.domain?.name)
        link = this.info.domain.name + this.info.identifier ?? "";

      let patternId = "";
      switch (this.info.type) {
        case "VisitCard":
          patternId = 244;
          break;
        case "menu":
          patternId = 246;
          break;
        default:
          patternId = 467;
          break;
      }

      let url = `${this.$corepayamakApi}/?token=${token}#/patternSms?link=${link}&pattern=${patternId}&user=${user}`;
      window.open(url, "_blank");
    },
    renderProductImage(item) {
      if (item.product?.gallery) {
        try {
          let img = JSON.parse(item.product.gallery)[0]?.images[0].url;
          return `<img alt="" src="${img}" class="rounded-pill" style="width: 42px;aspect-ratio: 1;object-fit: cover"/>`;
        } catch (e) {
          return "";
        }
      }
      return "";
    },
    printFactor() {
      setTimeout(() => {
        this.$nextTick(() => {
          const printableContent = document.getElementById("factor.print");
          const printWindow = window.open(
            "",
            "printwindow",
            "height=1200,width=1000"
          );
          printWindow.document.write(
            '<html><head><title>print preview</title><style>html{-webkit-print-color-adjust: exact;color-adjust: exact;} @page{size: letter;}</style></head><body style="margin: 0;padding: 0;height: 100vh;overflow: hidden;max-height: 100vh;display: flex;flex-direction: column;align-items: center;justify-content: space-between;">'
          );
          printWindow.document.write(printableContent.innerHTML);
          printWindow.document.write("</body></html>");
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 2000);
          printWindow.addEventListener("unload", () => {
            this.$emit("close");
          });
        });
      }, 50);
    },
  },
};
</script>

<style scoped>
.flip-card {
  background-color: transparent;
  perspective: 1000px;
}
.flip-card-inner {
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.6s;
  transform-style: preserve-3d;
}
.flip-card.flipped .flip-card-inner {
  transform: rotateY(180deg);
}
.flip-card-front,
.flip-card-back {
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
}
.flip-card-back {
  transform: rotateY(180deg);
}
.teal-lighten-3 {
  background-color: #80cbc4 !important;
  color: #fff !important;
}
.light-blue-lighten-3 {
  background-color: #4fc3f7 !important;
  color: #fff !important;
}
.grey-lighten-2 {
  background-color: #eeeeee !important;
  color: #424242 !important;
}
.teal-lighten-4 {
  background-color: #b2dfdb !important;
  color: #fff !important;
}
.amber-lighten-3 {
  background-color: #ffe082 !important;
  color: #424242 !important;
}
.cyan-lighten-4 {
  background-color: #b2ebf2 !important;
  color: #fff !important;
}
.red-lighten-4 {
  background-color: #ffcdd2 !important;
  color: #fff !important;
}
.grey-lighten-3 {
  background-color: #f5f5f5 !important;
  color: #424242 !important;
}
.table-striped >>> .status-chip {
  width: 100%;
  white-space: nowrap;
  padding: 2px 8px;
  text-align: center;
  border-radius: 4px;
  background-color: forestgreen;
  color: white;
}
</style>
